import { request } from "./transport";

export interface OptionChainRow {
  ticker: string;
  expiry: string;
  strike: number;
  contract_type: "call" | "put";
  contract_symbol: string;
  bid: number | null;
  ask: number | null;
  last_price: number | null;
  volume: number | null;
  open_interest: number | null;
  implied_volatility: number | null;
  in_the_money: boolean | null;
  last_trade_at: string | null;
  underlying_spot: number;
  snapshot_at: string;
}

export interface OptionIvBarRow {
  ticker: string;
  expiry: string;
  bar_at: string;
  underlying_spot: number;
  atm_strike: number;
  atm_call_iv: number | null;
  atm_put_iv: number | null;
  atm_iv_avg: number | null;
  atm_straddle_mid: number | null;
}

export interface Iv30dBarRow {
  ticker: string;
  bar_at: string;
  iv_30d: number;
  anchor_expiry_short: string;
  anchor_expiry_long: string;
  weight_short: number;
  // True for in-between minutes produced by Iv30Densifier; false (or
  // absent on legacy responses) for real cron-emitted anchors. Cable
  // broadcasts only carry real anchors, so the merge logic in
  // useIv30dHistory upgrades the corresponding minute from synthesized
  // to real on arrival.
  synthesized?: boolean;
}

// Read-only client. The browser never ingests — OptionChainRefreshJob
// (Sidekiq cron) is the only writer. /api/v1/option_chains/ingest
// exists server-side for internal callers but is not exposed here.
export const optionChains = {
  listOptionChain: (params: { ticker: string; expiry?: string }) => {
    const q = new URLSearchParams({ ticker: params.ticker });
    if (params.expiry) q.set("expiry", params.expiry);
    return request<OptionChainRow[]>(`/option_chains?${q.toString()}`);
  },

  // Without expiry → one row per (ticker, expiry), the latest bar each.
  // With expiry → up to `limit` most-recent bars for that (ticker, expiry),
  // oldest first.
  listOptionIvBars: (params: {
    ticker: string;
    expiry?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams({ ticker: params.ticker });
    if (params.expiry) q.set("expiry", params.expiry);
    if (params.limit) q.set("limit", String(params.limit));
    return request<OptionIvBarRow[]>(`/option_iv_bars?${q.toString()}`);
  },

  // Constant-30D IV per (ticker, bar_at), variance-time interpolated by
  // OptionChains::Iv30Synthesizer.
  //
  // Without `from` → tail fetch: most-recent `limit` real anchors,
  // oldest first. Used by the IV pill / scenario-IV ladder.
  //
  // With `from` → chart-history seed: server densifies the 20-min
  // anchors into a 1-min series via Iv30Densifier so the chart renders
  // at ~390 points across an RTH session. In-between minutes carry
  // `synthesized: true`.
  listIv30dBars: (params: {
    ticker: string;
    limit?: number;
    from?: string;
    to?: string;
  }) => {
    const q = new URLSearchParams({ ticker: params.ticker });
    if (params.limit) q.set("limit", String(params.limit));
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    return request<Iv30dBarRow[]>(`/iv_30d_bars?${q.toString()}`);
  },
};
